// src/shared/services/MockService.js
import ServiceInterface from './ServiceInterface';
import {
    mockUsers,
    mockActivities,
    mockCompetitions,
    calculatePoints
} from '../../mock/mockData';
import { ServiceError, ErrorTypes } from './types';
import { parseLocalDate } from '../utils/dateUtils';

class MockService extends ServiceInterface {
    constructor() {
        super();
        this.isAuthenticated = !!localStorage.getItem('token');
        this.currentUser = null;
    }

    // Helper Methods
    getUserName(username) {
        const user = mockUsers.find(u => u.username === username);
        return user ? `${user.firstName} ${user.lastName}` : 'Unknown User';
    }

    calculateUserPointsForCompetition(username, competition) {
        return mockActivities
            .filter(a =>
                a.username === username &&
                new Date(a.date) >= parseLocalDate(competition.startDate) &&
                new Date(a.date) <= parseLocalDate(competition.endDate)
            )
            .reduce((sum, act) => sum + act.points, 0);
    }

    calculateUserPosition(username, competition) {
        const competitorPoints = competition.competitors.map(competitorId => ({
            username: competitorId,
            points: this.calculateUserPointsForCompetition(competitorId, competition)
        }));

        competitorPoints.sort((a, b) => b.points - a.points);
        return competitorPoints.findIndex(c => c.username === username) + 1;
    }

    // Auth Methods
    async getCurrentUser() {
        console.log('calling: getCurrentUser')
        await this.delay(300);

        const token = localStorage.getItem('token');
        if (!token) return null;

        if (!this.currentUser) {
            const username = localStorage.getItem('username');
            this.currentUser = mockUsers.find(u => u.username === username);
        }

        return this.currentUser;
    }

    async login(credentials) {
        console.log('calling: login')
        await this.delay(800);

        const user = mockUsers.find(u => u.email === credentials.email);

        if (user) {
            this.isAuthenticated = true;
            this.currentUser = user;
            localStorage.setItem('token', 'mock-token');
            localStorage.setItem('username', user.username);
            return user;
        }

        throw new ServiceError(ErrorTypes.AUTH, 'Invalid credentials');
    }

    async logout() {
        console.log('calling: logout')
        await this.delay(300);
        this.isAuthenticated = false;
        this.currentUser = null;
        localStorage.removeItem('token');
        localStorage.removeItem('username');
        return { success: true };
    }

    // Activity Methods
    async getActivities(pageToken = undefined, pageSize = 10) {  // Changed from nextPageToken to pageToken
        await this.delay(500);

        // Calculate the start index based on the pageToken
        const startIndex = pageToken ? parseInt(pageToken) : 0;

        // Get all activities and add user data
        let allActivities = mockActivities.map(activity => {
            const user = mockUsers.find(u => u.username === activity.username);
            return {
                ...activity,
                userGivenName: user ? `${user.firstName} ${user.lastName}` : 'Unknown User',
                profilePicture: user?.profilePicture
            };
        });

        // Sort by date
        allActivities.sort((a, b) => new Date(b.date) - new Date(a.date));

        // Get the current page of activities
        const activities = allActivities.slice(startIndex, startIndex + pageSize);

        // Calculate if there are more activities and the next page token
        const hasMore = startIndex + pageSize < allActivities.length;
        const nextPageToken = hasMore ? (startIndex + pageSize).toString() : undefined;

        return {
            activities,
            nextPageToken,
            hasMore
        };
    }

    async fetchActivity(activityId) {
        console.log('calling: fetchActivity')
        await this.delay(500);

        const activity = mockActivities.find(a => a.activityId === activityId);
        if (!activity) {
            throw new ServiceError(ErrorTypes.NOT_FOUND, 'Activity not found');
        }

        const user = mockUsers.find(u => u.username === activity.username);
        if (!user) {
            throw new ServiceError(ErrorTypes.NOT_FOUND, 'User not found');
        }

        return {
            ...activity,
            userGivenName: `${user.firstName} ${user.lastName}`,
            profilePicture: user.profilePicture
        };
    }

    async createActivity(activityData) {
        console.log('calling: createActivity')
        await this.delay(800);

        const username = localStorage.getItem('username');
        this.currentUser = mockUsers.find(u => u.username === username);

        if (!this.currentUser) {
            throw new ServiceError(ErrorTypes.AUTH, 'User not authenticated');
        }

        const newActivity = {
            activityId: `act-${Date.now()}`,
            username: this.currentUser.username,
            userGivenName: `${this.currentUser.firstName} ${this.currentUser.lastName}`,
            ...activityData,
            points: calculatePoints(activityData)
        };

        mockActivities.unshift(newActivity);
        return newActivity;
    }

    async updateActivity(activityId, activityData) {
        console.log('calling: updateCompetition')
        await this.delay(500);

        const username = localStorage.getItem('username');
        this.currentUser = mockUsers.find(u => u.username === username);

        if (!this.currentUser) {
            throw new ServiceError(ErrorTypes.AUTH, 'User not authenticated');
        }

        const activityIndex = mockActivities.findIndex(a => a.activityId === activityId);
        if (activityIndex === -1) {
            throw new ServiceError(ErrorTypes.NOT_FOUND, 'Activity not found');
        }

        const activity = mockActivities[activityIndex];
        if (activity.username !== this.currentUser.username) {
            throw new ServiceError(ErrorTypes.AUTH, 'Not authorized to update this activity');
        }

        const updatedActivity = {
            ...activity,
            ...activityData,
            points: calculatePoints(activityData)
        };

        mockActivities[activityIndex] = updatedActivity;
        return updatedActivity;
    }

    async deleteActivity(activityId) {
        console.log('calling: deleteActivity')
        await this.delay(500);

        const username = localStorage.getItem('username');
        this.currentUser = mockUsers.find(u => u.username === username);

        if (!this.currentUser) {
            throw new ServiceError(ErrorTypes.AUTH, 'User not authenticated');
        }

        const activityIndex = mockActivities.findIndex(a => a.activityId === activityId);
        if (activityIndex === -1) {
            throw new ServiceError(ErrorTypes.NOT_FOUND, 'Activity not found');
        }

        const activity = mockActivities[activityIndex];
        if (activity.username !== this.currentUser.username) {
            throw new ServiceError(ErrorTypes.AUTH, 'Not authorized to delete this activity');
        }

        mockActivities.splice(activityIndex, 1);
        return { success: true };
    }

    async deleteActivityAsAdmin(activityId) {
        await this.delay(500);
        return { success: true, activityId };
    }

    async requestActivityExemption(activityId, note) {
        console.log('calling: requestActivityExemption')
        await this.delay(500);

        return {
            exemptionStatus: 'pending',
            exemptionRequestedAt: new Date().toISOString(),
            exemptionUserNote: note,
        };
    }

    async reviewActivityExemption(activityId, action, note) {
        console.log('calling: reviewActivityExemption')
        await this.delay(500);

        return {
            exemptionStatus: action === 'approve' ? 'approved' : 'rejected',
            exemptionReviewedBy: 'mock-admin',
            exemptionReviewedAt: new Date().toISOString(),
            exemptionAdminNote: note,
            penaltyFactor: action === 'approve' ? 1.0 : undefined,
        };
    }

    async reactToActivity(activityId, emoji) {
        await this.delay(200);
        const username = localStorage.getItem('username');
        const activity = mockActivities.find(a => a.activityId === activityId);
        if (!activity) throw new ServiceError(ErrorTypes.NOT_FOUND, 'Activity not found');
        if (!activity.reactions) activity.reactions = {};
        const users = activity.reactions[emoji] || [];
        if (users.includes(username)) {
            activity.reactions[emoji] = users.filter(u => u !== username);
        } else {
            activity.reactions[emoji] = [...users, username];
        }
        if (!activity.reactions[emoji].length) delete activity.reactions[emoji];
        return { reactions: { ...activity.reactions } };
    }

    // Competition Methods
    async getAllCompetitions(state = null) {
        console.log('calling: getAllCompetitions')
        await this.delay(500);

        const username = localStorage.getItem('username');
        this.currentUser = mockUsers.find(u => u.username === username);

        let competitions = mockCompetitions.map(competition => ({
            ...competition,
            isUserJoined: competition.competitors.includes(this.currentUser?.username)
        }));

        // Filter by state if provided
        if (state) {
            competitions = competitions.filter(c => c.state === state);
        }

        return competitions;
    }

    async fetchCompetition(competitionId) {
        console.log('calling: fetchCompetition')
        await this.delay(500);
        const competition = mockCompetitions.find(c => c.competitionId.toString() === competitionId);
        if (!competition) {
            throw new ServiceError(ErrorTypes.NOT_FOUND, 'Competition not found');
        }
        return competition;
    }

    async getCompetitionDetails(competitionId) {
        console.log('calling: getCompetitionDetails')
        await this.delay(500);
        if (!this.currentUser) {
            throw new ServiceError(ErrorTypes.AUTH, 'Unauthorized');
        }

        const competition = mockCompetitions.find(c => c.competitionId.toString() === competitionId);
        if (!competition) {
            throw new ServiceError(ErrorTypes.NOT_FOUND, 'Competition not found');
        }

        const competitors = competition.competitors.map(competitorId => {
            const profile = mockUsers.find(u => u.username === competitorId);
            const userActivities = mockActivities.filter(a =>
                a.username === competitorId &&
                new Date(a.date) >= parseLocalDate(competition.startDate) &&
                new Date(a.date) <= parseLocalDate(competition.endDate)
            );

            const totalPoints = userActivities.reduce((sum, act) => sum + act.points, 0);
            const meetsCriteria = totalPoints >= competition.pointTarget;
            const progress = (totalPoints / competition.pointTarget) * 100;

            return {
                username: profile.username,
                firstName: profile.firstName,
                lastName: profile.lastName,
                profilePicture: profile.profilePicture,
                totalPoints,
                numActivities: userActivities.length,
                meetsCriteria,
                progress: Math.min(progress, 100)
            };
        }).sort((a, b) => b.totalPoints - a.totalPoints);

        return {
            competition,
            competitors,
            isUserJoined: competition.competitors.includes(this.currentUser.username)
        };
    }

    async joinCompetition(currentUser, competitionId) {
        console.log('calling: joinCompetition')
        await this.delay(500);
        const competition = mockCompetitions.find(c => c.competitionId.toString() === competitionId.toString());

        if (!competition) {
            throw new ServiceError(ErrorTypes.NOT_FOUND, 'Competition not found');
        }

        if (competition.competitors.includes(currentUser.username)) {
            throw new ServiceError(ErrorTypes.VALIDATION, 'Already joined competition');
        }

        competition.competitors.push(currentUser.username);
        return { success: true };
    }

    async createCompetition(competitionData) {
        console.log('calling: createCompetition')
        await this.delay(500);

        const username = localStorage.getItem('username');
        if (!username) {
            throw new ServiceError(ErrorTypes.AUTH, 'User not authenticated');
        }

        const now = new Date();
        const startDate = parseLocalDate(competitionData.startDate);
        const endDate = parseLocalDate(competitionData.endDate);

        let state = 'upcoming';
        if (now >= startDate && now <= endDate) {
            state = 'active';
        } else if (now > endDate) {
            state = 'concluded';
        }

        const newCompetition = {
            competitionId: `comp-${Date.now()}`,
            name: competitionData.name,
            pointTarget: competitionData.pointTarget,
            startDate: competitionData.startDate,
            endDate: competitionData.endDate,
            state,
            competitors: [],
            ministerOfActivity: null
        };

        mockCompetitions.push(newCompetition);
        return {
            message: 'Competition created successfully',
            competition: newCompetition
        };
    }

    async updateCompetition(competitionId, competitionData) {
        console.log('calling: updateCompetition')
        await this.delay(500);

        const username = localStorage.getItem('username');
        if (!username) {
            throw new ServiceError(ErrorTypes.AUTH, 'User not authenticated');
        }

        const competitionIndex = mockCompetitions.findIndex(c => c.competitionId.toString() === competitionId.toString());
        if (competitionIndex === -1) {
            throw new ServiceError(ErrorTypes.NOT_FOUND, 'Competition not found');
        }

        const competition = mockCompetitions[competitionIndex];

        if (competition.state === 'concluded') {
            throw new ServiceError(ErrorTypes.VALIDATION, 'Cannot edit a concluded competition');
        }

        const now = new Date();
        const startDate = parseLocalDate(competitionData.startDate);
        const endDate = parseLocalDate(competitionData.endDate);

        let state = competition.state;
        if (now >= startDate && now <= endDate) {
            state = 'active';
        } else if (now > endDate) {
            state = 'concluded';
        } else {
            state = 'upcoming';
        }

        mockCompetitions[competitionIndex] = {
            ...competition,
            name: competitionData.name,
            pointTarget: competitionData.pointTarget,
            startDate: competitionData.startDate,
            endDate: competitionData.endDate,
            state
        };

        return {
            message: 'Competition updated successfully',
            competition: mockCompetitions[competitionIndex]
        };
    }

    async getCompetitionStats(competitionId) {
        console.log('calling: getCompetitionStats')
        await this.delay(500);
        const competition = mockCompetitions.find(c => c.competitionId.toString() === competitionId.toString());
        if (!competition) {
            throw new ServiceError(ErrorTypes.NOT_FOUND, 'Competition not found');
        }

        // Build cumulative points timeseries per user (matching backend format)
        const cumulativePointsTimeseries = {};
        let totalActivities = 0;
        let totalPoints = 0;

        const activityTypeBreakdown = {};

        competition.competitors.forEach(username => {
            const userActivities = mockActivities.filter(a =>
                a.username === username &&
                new Date(a.date) >= parseLocalDate(competition.startDate) &&
                new Date(a.date) <= parseLocalDate(competition.endDate)
            ).sort((a, b) => new Date(a.date) - new Date(b.date));

            totalActivities += userActivities.length;
            activityTypeBreakdown[username] = {};

            // Group points by date
            const pointsByDate = {};
            userActivities.forEach(activity => {
                totalPoints += activity.points;
                if (!pointsByDate[activity.date]) {
                    pointsByDate[activity.date] = 0;
                }
                pointsByDate[activity.date] += activity.points;

                const type = activity.activityType;
                if (!activityTypeBreakdown[username][type]) {
                    activityTypeBreakdown[username][type] = { count: 0, points: 0 };
                }
                activityTypeBreakdown[username][type].count += 1;
                activityTypeBreakdown[username][type].points += activity.points;
            });

            // Build cumulative timeseries
            const sortedDates = Object.keys(pointsByDate).sort();
            let cumulative = 0;
            cumulativePointsTimeseries[username] = sortedDates.map(date => {
                cumulative += pointsByDate[date];
                return { date, points: cumulative };
            });
        });

        return {
            competition: {
                startDate: competition.startDate,
                endDate: competition.endDate
            },
            totalActivities,
            totalPoints: totalPoints.toString(),
            cumulativePointsTimeseries,
            activityTypeBreakdown
        };
    }

    async getCompetitorHistory(competitionId, username) {
        console.log('calling: getCompetitorHistory')
        await this.delay(500);
        const competition = mockCompetitions.find(c => c.competitionId === competitionId);
        if (!competition) {
            throw new ServiceError(ErrorTypes.NOT_FOUND, 'Competition not found');
        }

        const competitor = competition.competitors.find(c => c.username === username);
        if (!competitor) {
            throw new ServiceError(ErrorTypes.NOT_FOUND, 'Competitor not found');
        }

        return {
            pointsHistory: [
                { date: '2024-01-01', points: 0 },
                { date: '2024-01-15', points: competitor.totalPoints / 2 },
                { date: '2024-01-30', points: competitor.totalPoints }
            ]
        };
    }

    async getCompetitionRules() {
        console.log('calling: getCompetitionRules')
        await this.delay(300);
        return {
            pointCalculations: {
                running: { basePoints: 1, elevationFactor: 0.01 },
                cycling: { basePoints: 0.25, elevationFactor: 0.005 },
                swimming: { basePoints: 4, elevationFactor: 0 },
                hiking: { basePoints: 0.5, elevationFactor: 0.005 }
            },
            minimumRequirements: {
                running: { distance: 2, pace: 9 },
                cycling: { distance: 10, speed: 18 },
                swimming: { distance: 0.1 },
                hiking: { distance: 3, pace: 20 }
            }
        };
    }

    // Profile Methods
    async fetchProfile(username) {
        console.log('calling: fetchProfile')
        await this.delay(500);
        const user = mockUsers.find(u => u.username.toString() === username);
        if (!user) {
            throw new ServiceError(ErrorTypes.NOT_FOUND, 'User not found');
        }
        return user;
    }

    async getProfile(username) {
        console.log('calling: getProfile')
        await this.delay(500);

        const user = mockUsers.find(u => u.username === username);
        if (!user) {
            throw new ServiceError(ErrorTypes.NOT_FOUND, 'User not found');
        }

        const userActivities = mockActivities.filter(a => a.username === username);
        const activeCompetitions = mockCompetitions
            .filter(c =>
                c.state === 'active' &&
                c.competitors.includes(username)
            )
            .map(c => {
                const competitionActivities = userActivities.filter(a =>
                    new Date(a.date) >= parseLocalDate(c.startDate) &&
                    new Date(a.date) <= parseLocalDate(c.endDate)
                );
                return {
                    competitionId: c.competitionId,
                    name: c.name,
                    points: competitionActivities.reduce((sum, act) => sum + act.points, 0)
                };
            });

        const recentActivities = [...userActivities]
            .sort((a, b) => new Date(b.date) - new Date(a.date))
            .slice(0, 3);

        const now = new Date();
        const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

        const buildActivitiesByType = (activities) => activities.reduce((acc, act) => {
            const type = act.activityType;
            if (!acc[type]) acc[type] = { count: 0, distance: 0, points: 0, duration: 0 };
            acc[type].count += 1;
            acc[type].distance += act.distance;
            acc[type].points += act.points;
            acc[type].duration += act.duration;
            return acc;
        }, {});

        // Get won competitions (where user is the winner)
        // Mock: filter concluded competitions where user is winner
        const wonCompetitions = mockCompetitions
            .filter(c =>
                c.state === 'concluded' &&
                c.winner === username
            )
            .map(c => {
                // Calculate user's points for this competition
                const userActivities = mockActivities.filter(a =>
                    a.username === username &&
                    new Date(a.date) >= parseLocalDate(c.startDate) &&
                    new Date(a.date) <= parseLocalDate(c.endDate)
                );
                const points = userActivities.reduce((sum, act) => sum + act.points, 0);

                return {
                    competitionId: c.competitionId,
                    name: c.name,
                    points: points,
                    endDate: c.endDate
                };
            });

        return {
            user: {
                username: user.username,
                firstName: user.firstName,
                lastName: user.lastName,
                profilePicture: user.profilePicture
            },
            totalActivities: userActivities.length,
            totalDistance: userActivities.reduce((sum, act) => sum + act.distance, 0),
            totalPoints: userActivities.reduce((sum, act) => sum + act.points, 0),
            competitionsWon: wonCompetitions.length,
            wonCompetitions: wonCompetitions,
            currentCompetitions: activeCompetitions,
            activitiesByType: buildActivitiesByType(userActivities),
            activitiesByTypeMonth: buildActivitiesByType(userActivities.filter(a => new Date(a.date) >= monthAgo)),
            activitiesByTypeWeek: buildActivitiesByType(userActivities.filter(a => new Date(a.date) >= weekAgo)),
            recentActivities: recentActivities.map(activity => ({
                activityId: activity.activityId,
                activityType: activity.activityType,
                date: activity.date,
                distance: activity.distance,
                elevation: activity.elevation,
                duration: activity.duration,
                points: activity.points
            }))
        };
    }

    async updateProfile(username, profileData) {
        console.log('calling: updateProfile')
        await this.delay(500);

        this.currentUser = mockUsers.find(u => u.username === username);

        if (!this.currentUser || this.currentUser.username !== username) {
            throw new ServiceError(ErrorTypes.AUTH, 'Unauthorized');
        }

        const userIndex = mockUsers.findIndex(u => u.username === username);
        if (userIndex === -1) {
            throw new ServiceError(ErrorTypes.NOT_FOUND, 'User not found');
        }

        mockUsers[userIndex] = {
            ...mockUsers[userIndex],
            ...profileData
        };

        return mockUsers[userIndex];
    }

    async getAllUsers({ pageSize = 20, nextToken = null, filters = {} } = {}) {
        await this.delay(400);

        let allUsers = mockUsers.map(user => {
            const userActivities = mockActivities.filter(a => a.username === user.username);
            return {
                ...user,
                totalActivities: userActivities.length,
                totalDistance: userActivities.reduce((sum, a) => sum + a.distance, 0),
                totalPoints: userActivities.reduce((sum, a) => sum + a.points, 0),
                activeCompetitionsCount: mockCompetitions
                    .filter(c => c.state === 'active' && c.competitors.includes(user.username))
                    .length,
            };
        });

        // Apply filters client-side for mock mode
        if (filters.activeInCompetition) {
            allUsers = allUsers.filter(u => u.activeCompetitionsCount > 0);
        }
        if (filters.stravaConnected) {
            // Mock: assume first 3 users have Strava connected
            const stravaUsernames = new Set(mockUsers.slice(0, 3).map(u => u.username));
            allUsers = allUsers.filter(u => stravaUsernames.has(u.username));
        }
        if (filters.notInCompetition) {
            allUsers = allUsers.filter(u => u.activeCompetitionsCount === 0);
        }

        const startIndex = nextToken ? parseInt(nextToken, 10) : 0;
        const page = allUsers.slice(startIndex, startIndex + pageSize);
        const hasNext = startIndex + pageSize < allUsers.length;

        return {
            users: page,
            nextToken: hasNext ? String(startIndex + pageSize) : null
        };
    }

    // Dashboard Methods
    async getFitHubData() {
        console.log('calling: getFitHubData')
        await this.delay(500);

        const username = localStorage.getItem('username');
        this.currentUser = mockUsers.find(u => u.username === username);

        if (!this.currentUser) {
            throw new ServiceError(ErrorTypes.AUTH, 'Unauthorized');
        }

        const recentActivities = mockActivities
            .filter(a => a.username === this.currentUser.username)
            .sort((a, b) => new Date(b.date) - new Date(a.date))
            .slice(0, 5);

        const personalStats = {
            totalActivities: recentActivities.length,
            totalDistance: recentActivities.reduce((sum, act) => sum + act.distance, 0),
            totalPoints: recentActivities.reduce((sum, act) => sum + act.points, 0),
            activitiesByType: recentActivities.reduce((acc, act) => {
                acc[act.activityType] = (acc[act.activityType] || 0) + 1;
                return acc;
            }, {})
        };

        const activeCompetitions = mockCompetitions
            .filter(c => c.state === 'active')
            .map(competition => ({
                competitionId: competition.competitionId,
                name: competition.name,
                endDate: competition.endDate,
                userPoints: this.calculateUserPointsForCompetition(
                    this.currentUser.username,
                    competition
                ),
                pointTarget: competition.pointTarget,
                currentPosition: this.calculateUserPosition(
                    this.currentUser.username,
                    competition
                ),
                isUserJoined: competition.competitors.includes(this.currentUser.username)
            }));

        const milestones = this.calculateMilestones();

        return {
            activeCompetitions,
            recentActivities: recentActivities.map(activity => ({
                ...activity,
                userGivenName: this.getUserName(activity.username)
            })),
            personalStats,
            milestones
        };
    }

    // Admin Methods
    async getAdminStats() {
        console.log('calling: getAdminStats')
        await this.delay(500);

        return {
            totalUsers: 42,
            activeCompetitions: 2,
            upcomingCompetitions: 1,
            concludedCompetitions: 5,
            pendingExemptions: 3,
            totalActivities: 1547,
            totalDistance: 28453.7,
        };
    }

    async getAdminExemptions({ status, pageSize = 20, nextToken } = {}) {
        await this.delay(500);

        const mockExemptions = [
            {
                activityId: 'act-100',
                username: 'john.doe',
                firstName: 'John',
                lastName: 'Doe',
                activityType: 'Running',
                distance: 5.2,
                date: '2025-03-10T08:00:00Z',
                rawPoints: 10,
                penaltyFactor: 2,
                points: 5,
                exemptionStatus: 'pending',
                exemptionRequestedAt: '2025-03-10T08:30:00Z',
                exemptionUserNote: 'Woke up with flu symptoms',
            },
            {
                activityId: 'act-101',
                username: 'jane.smith',
                firstName: 'Jane',
                lastName: 'Smith',
                activityType: 'Cycling',
                distance: 30,
                date: '2025-03-11T14:30:00Z',
                rawPoints: 15,
                penaltyFactor: 1.5,
                points: 10,
                exemptionStatus: 'pending',
                exemptionRequestedAt: '2025-03-11T15:00:00Z',
                exemptionUserNote: 'Bike had a flat tire',
            },
            {
                activityId: 'act-102',
                username: 'bob.wilson',
                firstName: 'Bob',
                lastName: 'Wilson',
                activityType: 'Swimming',
                distance: 2,
                date: '2025-03-08T06:00:00Z',
                rawPoints: 8,
                penaltyFactor: 1,
                points: 8,
                exemptionStatus: 'approved',
                exemptionRequestedAt: '2025-03-08T06:30:00Z',
                exemptionUserNote: 'Pool was closed for maintenance',
                exemptionAdminNote: 'Approved - valid reason',
                exemptionReviewedBy: 'admin',
                exemptionReviewedAt: '2025-03-09T10:00:00Z',
            },
            {
                activityId: 'act-103',
                username: 'alice.jones',
                firstName: 'Alice',
                lastName: 'Jones',
                activityType: 'Hiking',
                distance: 12,
                date: '2025-03-07T12:00:00Z',
                rawPoints: 6,
                penaltyFactor: 1.2,
                points: 5,
                exemptionStatus: 'rejected',
                exemptionRequestedAt: '2025-03-07T12:30:00Z',
                exemptionUserNote: 'Lost track of time',
                exemptionAdminNote: 'Rejected - not a valid reason',
                exemptionReviewedBy: 'admin',
                exemptionReviewedAt: '2025-03-08T09:00:00Z',
            },
            {
                activityId: 'act-104',
                username: 'charlie.brown',
                firstName: 'Charlie',
                lastName: 'Brown',
                activityType: 'Running',
                distance: 8,
                date: '2025-03-12T18:00:00Z',
                rawPoints: 12,
                penaltyFactor: 1.5,
                points: 8,
                exemptionStatus: 'pending',
                exemptionRequestedAt: '2025-03-12T18:30:00Z',
                exemptionUserNote: 'Public transport strike',
            },
        ];

        let filtered = mockExemptions;
        if (status && status !== 'all') {
            filtered = filtered.filter(e => e.exemptionStatus === status);
        }

        // Sort by exemptionRequestedAt descending (newest first)
        filtered.sort((a, b) => new Date(b.exemptionRequestedAt) - new Date(a.exemptionRequestedAt));

        const startIndex = nextToken ? parseInt(nextToken, 10) : 0;
        const page = filtered.slice(startIndex, startIndex + pageSize);
        const hasNext = startIndex + pageSize < filtered.length;

        return {
            exemptions: page,
            nextToken: hasNext ? String(startIndex + pageSize) : null,
        };
    }

    async reviewExemption(activityId, { action, note } = {}) {
        await this.delay(500);

        return {
            success: true,
            activityId,
            exemptionStatus: action === 'approve' ? 'approved' : 'rejected',
            reviewedBy: 'mock-admin',
            reviewedAt: new Date().toISOString(),
            adminNote: note,
        };
    }

    async syncCompetitionStates() {
        await this.delay(500);
        return {
            success: true,
            summary: {
                timestamp: new Date().toISOString(),
                updated: 2,
                unchanged: 3,
                errors: 0,
                transitions: { to_upcoming: 0, to_active: 1, to_concluded: 1 }
            }
        };
    }

    async forceCompetitionState(competitionId, state) {
        await this.delay(500);
        return {
            success: true,
            competitionId,
            previousState: 'active',
            newState: state
        };
    }

    async adminResyncStrava(username, after, before = null, overwrite = false, ignorePenalty = true) {
        await this.delay(800);
        if (!username) {
            const users = [
                { username: 'alice', total: 12, synced: 8, skipped: 4, errors: 0 },
                { username: 'bob', total: 5, synced: 5, skipped: 0, errors: 0 },
                { username: 'carol', total: 3, synced: 0, skipped: 3, errors: 0 },
            ];
            return {
                success: true,
                after,
                users,
                totals: {
                    total: 20, synced: 13, skipped: 7, errors: 0,
                },
            };
        }
        return {
            success: true,
            username,
            after,
            total: 12,
            synced: 8,
            skipped: 4,
            errors: 0,
        };
    }

    async getExemptionCounts() {
        await this.delay(300);
        return {
            pending: 3,
            approved: 1,
            rejected: 1,
        };
    }

    // Private helper method for calculating milestones
    calculateMilestones() {
        const activeCompetitions = mockCompetitions
            .filter(c => c.state === 'active')
            .map(comp => ({
                competitionId: comp.competitionId,
                name: comp.name,
                userPoints: this.calculateUserPointsForCompetition(
                    this.currentUser.username,
                    comp
                ),
                pointTarget: comp.pointTarget,
                isUserJoined: comp.competitors.includes(this.currentUser.username)
            }));

        const personalBests = ['running', 'cycling', 'swimming']
            .map(type => {
                const activities = mockActivities.filter(
                    a => a.username === this.currentUser.username &&
                        a.activityType === type
                );

                const bestActivity = activities.reduce((best, current) =>
                    current.distance > (best?.distance || 0) ? current : best,
                    null
                );

                const weekStart = new Date();
                weekStart.setDate(weekStart.getDate() - 7);
                const thisWeeksActivities = activities.filter(
                    a => new Date(a.date) >= weekStart
                );

                const currentBestActivity = thisWeeksActivities.reduce((best, current) =>
                    current.distance > (best?.distance || 0) ? current : best,
                    null
                );

                const currentWeekDistance = thisWeeksActivities.reduce(
                    (sum, a) => sum + a.distance,
                    0
                );

                return {
                    type,
                    current: currentWeekDistance,
                    currentActivityId: currentBestActivity?.activityId,
                    best: bestActivity ? bestActivity.distance : 0,
                    bestActivityId: bestActivity?.activityId
                };
            })
            .filter(record => record.best > 0);

        return {
            activeCompetitions,
            personalBests
        };
    }

    async getActivityRoutes() {
        await this.delay(300);
        return [];
    }

    async parseActivityFile(filename, fileData) {
        await this.delay(500);
        return { activityType: null, distance: 0, duration: 0, elevation: 0, date: null, routePolyline: null };
    }
}

export default MockService;
